import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { CheckCircle2, ExternalLink, Send, TriangleAlert } from 'lucide-react';
import axiosInstance from '../utils/axiosInstance';
import V2Shell from './V2Shell';

const GORGIAS = 'https://lamptwist.gorgias.com/app/ticket/';

// Opened from the "Send to customer" button in a Gorgias note. Shows the
// email exactly as it will go out, signed with your name, and sends it as
// you after every check passes again on the server.
const SendV2 = () => {
  const { runId } = useParams();
  const [p, setP] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(null);

  useEffect(() => {
    axiosInstance.get(`api/replai/v2/send/${runId}/`)
      .then(({ data }) => { setP(data); if (data.sent?.status === 'sent') setSent(data.sent); })
      .catch((e) => setError(e.response?.data?.error || 'This reply could not be loaded.'));
  }, [runId]);

  const send = async () => {
    setBusy(true);
    setError('');
    try {
      const { data } = await axiosInstance.post(`api/replai/v2/send/${runId}/`);
      setSent(data.sent);
    } catch (e) {
      setError(e.response?.data?.error || 'Not sent. Try again in a moment.');
    }
    setBusy(false);
  };

  const blocked = p && p.blockers.length > 0;
  return (
    <V2Shell>
      <div className="mx-auto max-w-3xl px-6 py-10">
        <h1 className="text-[2rem] font-semibold leading-tight">Send to customer</h1>
        {!p && !error && <div className="mt-6 h-64 animate-pulse rounded-lg bg-[var(--line-2)]" />}
        {p && (
          <>
            <p className="mt-2 text-[var(--ink-2)]">
              R-{p.run} · <a className="inline-flex items-center gap-1 hover:underline" href={`${GORGIAS}${p.ticket}`} target="_blank" rel="noreferrer">
                Gorgias ticket {p.ticket} <ExternalLink size={14} /></a>
            </p>
            <dl className="mt-6 grid grid-cols-[6rem_1fr] gap-y-1.5 text-[0.95rem]">
              <dt className="text-[var(--ink-3)]">From</dt><dd>{p.sender ? `${p.sender.name} (${p.from})` : p.from}</dd>
              <dt className="text-[var(--ink-3)]">To</dt><dd>{p.to_name ? `${p.to_name} <${p.to}>` : p.to}</dd>
              <dt className="text-[var(--ink-3)]">Subject</dt><dd>{p.subject}</dd>
            </dl>
            <div className="mt-4 rounded-lg border border-[var(--line)] bg-white p-5 text-[0.98rem] leading-relaxed text-[#1f2328] [&_p]:mb-3"
              dangerouslySetInnerHTML={{ __html: p.html }} />
          </>
        )}

        {sent ? (
          <div className="mt-6 flex items-start gap-3 rounded-lg border border-green-200 bg-green-50 p-4 text-green-900">
            <CheckCircle2 className="mt-0.5 shrink-0" size={20} />
            <div>
              <div className="font-semibold">Sent by {sent.by_name}</div>
              <div className="text-sm">{sent.closed === false ? 'The reply is in the Gorgias ticket, but closing it failed: close it in Gorgias.' : 'The reply is in the Gorgias ticket and the ticket is closed. You can close this tab.'}</div>
            </div>
          </div>
        ) : (
          <>
            {blocked && (
              <div className="mt-6 rounded-lg border border-amber-200 bg-amber-50 p-4 text-amber-900">
                <div className="flex items-center gap-2 font-semibold"><TriangleAlert size={18} /> Not sent from here</div>
                <ul className="mt-2 list-disc pl-5 text-sm">{p.blockers.map((b) => <li key={b}>{b}</li>)}</ul>
              </div>
            )}
            {error && <p className="mt-4 rounded-md bg-red-50 p-3 text-sm text-red-800">{error}</p>}
            {p && !blocked && (
              <div className="mt-6 flex flex-wrap items-center gap-4">
                <button type="button" onClick={send} disabled={busy}
                  className="inline-flex items-center gap-2 rounded-md bg-[#166534] px-5 py-2.5 font-semibold text-white hover:bg-[#14532d] disabled:opacity-60">
                  <Send size={17} /> {busy ? 'Sending…' : `Send as ${p.sender?.name || 'me'} & close`}
                </button>
                <span className="text-sm text-[var(--ink-3)]">It goes out in the Gorgias thread, signed with your name, and the ticket is closed.</span>
              </div>
            )}
          </>
        )}
        <p className="mt-10 text-sm"><Link to={`/tickets/${runId}`} className="text-[var(--ink-2)] hover:underline">Open this note in Replai</Link></p>
      </div>
    </V2Shell>
  );
};

export default SendV2;
